import { motion, useScroll, useTransform } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SerifGlowInView } from "./SerifGlow";
import { supabase, getPrimaryImage, getEffectivePrice, formatPrice, type Product } from "@/lib/supabase";

const ASSET = "https://qclay.design/lovable/bags";

const ENV_W = 480;
const ENV_H = 340;
const FLAP_H = 200;

// Fallback photos when DB is empty — each URL is a standalone full string
const FALLBACK_PHOTOS = [
  {
    img: "https://readdy.ai/api/search-image?query=Classic%20vintage%20wooden%20rocking%20horse%20children%20toy%20displayed%20as%20a%20polaroid%20instant%20photo%20with%20thick%20white%20paper%20border%20and%20handwritten%20caption%20area%2C%20soft%20natural%20studio%20lighting%2C%20warm%20cream%20pastel%20background%20with%20subtle%20dried%20flowers%20and%20autumn%20foliage%2C%20nostalgic%20editorial%20product%20photography%2C%20cozy%20nursery%20aesthetic%2C%20highly%20detailed%20realistic%20texture%2C%20centered%20composition%2C%20soft%20shadows&width=600&height=800&seq=toy-polaroid-01-rocking-horse&orientation=portrait",
    name: "Rocky",
  },
  {
    img: "https://readdy.ai/api/search-image?query=Cute%20small%20red%20wooden%20toy%20car%20for%20children%20displayed%20as%20a%20polaroid%20instant%20photo%20with%20thick%20white%20paper%20border%20and%20handwritten%20caption%20area%2C%20soft%20natural%20studio%20lighting%2C%20warm%20cream%20pastel%20background%20with%20subtle%20dried%20flowers%20and%20autumn%20foliage%2C%20nostalgic%20editorial%20product%20photography%2C%20cozy%20nursery%20aesthetic%2C%20highly%20detailed%20realistic%20texture%2C%20centered%20composition%2C%20soft%20shadows&width=600&height=800&seq=toy-polaroid-02-red-toy-car&orientation=portrait",
    name: "Zippy",
  },
  {
    img: "https://readdy.ai/api/search-image?query=Colorful%20wooden%20building%20blocks%20children%20toy%20stacked%20in%20a%20small%20tower%20displayed%20as%20a%20polaroid%20instant%20photo%20with%20thick%20white%20paper%20border%20and%20handwritten%20caption%20area%2C%20soft%20natural%20studio%20lighting%2C%20warm%20cream%20pastel%20background%20with%20subtle%20dried%20flowers%20and%20autumn%20foliage%2C%20nostalgic%20editorial%20product%20photography%2C%20cozy%20nursery%20aesthetic%2C%20highly%20detailed%20realistic%20texture%2C%20centered%20composition%2C%20soft%20shadows&width=600&height=800&seq=toy-polaroid-03-wooden-blocks&orientation=portrait",
    name: "Blocky",
  },
  {
    img: "https://readdy.ai/api/search-image?query=Fluffy%20soft%20brown%20teddy%20bear%20children%20plush%20toy%20displayed%20as%20a%20polaroid%20instant%20photo%20with%20thick%20white%20paper%20border%20and%20handwritten%20caption%20area%2C%20soft%20natural%20studio%20lighting%2C%20warm%20cream%20pastel%20background%20with%20subtle%20dried%20flowers%20and%20autumn%20foliage%2C%20nostalgic%20editorial%20product%20photography%2C%20cozy%20nursery%20aesthetic%2C%20highly%20detailed%20realistic%20texture%2C%20centered%20composition%2C%20soft%20shadows&width=600&height=800&seq=toy-polaroid-04-teddy-bear&orientation=portrait",
    name: "Teddy",
  },
  {
    img: "https://readdy.ai/api/search-image?query=Charming%20wooden%20toy%20train%20engine%20with%20colorful%20carriages%20children%20toy%20displayed%20as%20a%20polaroid%20instant%20photo%20with%20thick%20white%20paper%20border%20and%20handwritten%20caption%20area%2C%20soft%20natural%20studio%20lighting%2C%20warm%20cream%20pastel%20background%20with%20subtle%20dried%20flowers%20and%20autumn%20foliage%2C%20nostalgic%20editorial%20product%20photography%2C%20cozy%20nursery%20aesthetic%2C%20highly%20detailed%20realistic%20texture%2C%20centered%20composition%2C%20soft%20shadows&width=600&height=800&seq=toy-polaroid-05-wooden-train&orientation=portrait",
    name: "Choo",
  },
  {
    img: "https://readdy.ai/api/search-image?query=Handmade%20fabric%20rag%20doll%20with%20yarn%20hair%20and%20cute%20dress%20children%20plush%20toy%20displayed%20as%20a%20polaroid%20instant%20photo%20with%20thick%20white%20paper%20border%20and%20handwritten%20caption%20area%2C%20soft%20natural%20studio%20lighting%2C%20warm%20cream%20pastel%20background%20with%20subtle%20dried%20flowers%20and%20autumn%20foliage%2C%20nostalgic%20editorial%20product%20photography%2C%20cozy%20nursery%20aesthetic%2C%20highly%20detailed%20realistic%20texture%2C%20centered%20composition%2C%20soft%20shadows&width=600&height=800&seq=toy-polaroid-06-rag-doll&orientation=portrait",
    name: "Lulu",
  },
];

const CARD_Z = [2, 4, 6, 6, 4, 2];

const PEEK: Array<[number, number, number]> = [
  [-90, -30, -12],
  [-40, -60, -6],
  [-15, -78, -2],
  [20, -76, 3],
  [55, -58, 7],
  [95, -28, 12],
];

const END: Array<[number, number, number]> = [
  [-625, 0, 0],
  [-375, 0, 0],
  [-125, 0, 0],
  [125, 0, 0],
  [375, 0, 0],
  [625, 0, 0],
];

const OFF = [0, 0.015, 0.03, 0.045, 0.06, 0.075];

interface CollectionPhoto {
  id?: number;
  img: string;
  name: string;
  price?: string;
}

export default function Collection() {
  const containerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });

  const [photos, setPhotos] = useState<CollectionPhoto[]>(
    FALLBACK_PHOTOS.map((p) => ({ img: p.img, name: p.name, price: "€129.90" })),
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error } = await supabase
          .from("product_items")
          .select("*, product_categories(id, name, sort_order)")
          .eq("status", "active")
          .order("id", { ascending: true })
          .limit(6);
        if (cancelled) return;
        if (error) throw error;
        if (data && data.length > 0) {
          const list = (data as Product[]).map((p) => ({
            id: p.id,
            img: getPrimaryImage(p),
            name: (p.name.split("—")[0] || p.name).trim(),
            price: formatPrice(getEffectivePrice(p), p.currency),
          }));
          // Ensure we always have 6 entries for the layout — pad from fallback if fewer.
          while (list.length < 6) {
            const fb = FALLBACK_PHOTOS[list.length];
            list.push({ img: fb.img, name: fb.name, price: "€129.90" });
          }
          setPhotos(list);
        }
      } catch (e) {
        console.warn("Collection product fetch failed", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const envelopeY = useTransform(
    scrollYProgress,
    [0, 0.18, 0.45, 0.7, 1],
    [145, 20, 90, 600, 900],
  );
  const envelopeIn = useTransform(scrollYProgress, [0.6, 0.75], [1, 0]);
  const flapRotate = useTransform(scrollYProgress, [0.2, 0.45], [180, 0]);
  const photoWrapperY = useTransform(envelopeY, (v) => -v);

  const [cardsOut, setCardsOut] = useState(false);
  const [cardsVisible, setCardsVisible] = useState(false);
  const [photosAboveFlap, setPhotosAboveFlap] = useState(false);

  useEffect(() => {
    const unsub1 = scrollYProgress.on("change", (v) => {
      setCardsOut(v >= 0.52);
      setCardsVisible(v >= 0.3);
    });
    const unsub2 = envelopeY.on("change", (v) => {
      setPhotosAboveFlap(v > 85);
    });
    return () => {
      unsub1();
      unsub2();
    };
  }, [scrollYProgress, envelopeY]);

  return (
    <section
      ref={containerRef}
      style={{
        background: "#111111",
        position: "relative",
        height: "400vh",
        fontFamily: "'Inter Tight', sans-serif",
      }}
    >
      <div
        className="flex flex-col items-center justify-center"
        style={{
          position: "sticky",
          top: 0,
          height: "100vh",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, filter: "blur(12px)", y: 20 }}
          whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          className="flex flex-col items-center text-center"
          style={{
            position: "absolute",
            top: 60,
            left: 0,
            right: 0,
            zIndex: 20,
          }}
        >
          <div className="flex" style={{ alignItems: "baseline", gap: 14 }}>
            <span
              style={{
                fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
                fontSize: 76,
                fontWeight: 600,
                lineHeight: 1,
                letterSpacing: "-2px",
                color: "#FFFFFF",
              }}
            >
              Our
            </span>
            <SerifGlowInView
              word="new"
              fontSize={78}
              lineHeight={78}
              letterSpacing={-3}
              strokeWidth={16}
              italic
              delay={0.5}
            />
          </div>
          <div
            style={{
              fontFamily: "'Fredoka', 'Inter Tight', sans-serif",
              fontSize: 76,
              fontWeight: 600,
              lineHeight: 1,
              letterSpacing: "-2px",
              color: "#FFFFFF",
              marginTop: 6,
            }}
          >
            Collection
          </div>
          <motion.p
            initial={{ opacity: 0, filter: "blur(8px)", y: 10 }}
            whileInView={{ opacity: 1, filter: "blur(0px)", y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.3 }}
            style={{
              maxWidth: 320,
              fontFamily: "'Inter Tight', sans-serif",
              fontSize: 13,
              fontWeight: 400,
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.45)",
              marginTop: 14,
              textAlign: "center",
            }}
          >
            Crafted with care and designed to follow you from day to night, it holds not only your essentials, but your stories
          </motion.p>
        </motion.div>

        {/* Envelope stage */}
        <motion.div
          style={{
            position: "absolute",
            top: "58%",
            left: "50%",
            width: ENV_W,
            height: ENV_H,
            marginLeft: -ENV_W / 2,
            marginTop: -ENV_H / 2,
            overflow: "visible",
            y: envelopeY,
          }}
        >
          {/* body (back panel) */}
          <motion.img
            src={`${ASSET}/envelop.webp`}
            alt=""
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: ENV_W,
              height: ENV_H,
              zIndex: 1,
              pointerEvents: "none",
              opacity: envelopeIn,
            }}
          />

          {/* left side flap */}
          <motion.img
            src={`${ASSET}/tapa-left.webp`}
            alt=""
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              height: ENV_H,
              width: "auto",
              zIndex: 3,
              pointerEvents: "none",
              opacity: envelopeIn,
            }}
          />

          {/* right side flap */}
          <motion.img
            src={`${ASSET}/tapa-right.webp`}
            alt=""
            style={{
              position: "absolute",
              top: 0,
              right: 0,
              height: ENV_H,
              width: "auto",
              zIndex: 3,
              pointerEvents: "none",
              opacity: envelopeIn,
            }}
          />

          {/* bottom flap */}
          <motion.img
            src={`${ASSET}/tapa-bajo.webp`}
            alt=""
            style={{
              position: "absolute",
              bottom: 0,
              left: 0,
              width: ENV_W,
              zIndex: 4,
              transformOrigin: "bottom center",
              transformPerspective: 1400,
              rotateX: 0,
              pointerEvents: "none",
              opacity: envelopeIn,
            }}
          />

          {/* triangular top flap */}
          <motion.div
            style={{
              position: "absolute",
              top: -FLAP_H + 5,
              left: 0,
              width: ENV_W,
              height: FLAP_H,
              transformOrigin: "bottom center",
              transformPerspective: 1400,
              rotateX: flapRotate,
              opacity: envelopeIn,
              zIndex: 8,
              pointerEvents: "none",
            }}
          >
            <img
              src={`${ASSET}/open-top.webp`}
              alt=""
              style={{ width: "100%", height: "100%", display: "block" }}
            />
          </motion.div>

          {/* Photo wrapper — stays centered in viewport by counter-translating */}
          <motion.div
            style={{
              position: "absolute",
              top: "50%",
              left: "50%",
              width: 0,
              height: 0,
              pointerEvents: "none",
              display: cardsVisible ? "block" : "none",
              opacity: 1,
              overflow: "visible",
              zIndex: photosAboveFlap || cardsOut ? 999 : 2,
              y: photoWrapperY,
            }}
          >
            {photos.map((p, i) => (
              <PhotoCard
                key={p.id ?? p.img}
                index={i}
                scrollYProgress={scrollYProgress}
                photo={p}
                cardsOut={cardsOut}
              />
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}

interface PhotoCardProps {
  index: number;
  scrollYProgress: ReturnType<typeof useScroll>["scrollYProgress"];
  photo: CollectionPhoto;
  cardsOut: boolean;
}

function PhotoCard({ index, scrollYProgress, photo, cardsOut }: PhotoCardProps) {
  const off = OFF[index];
  const a0 = 0.3 + off;
  const a1 = 0.5 + off;
  const b0 = 0.55 + off;
  const b1 = 0.78 + off;

  const [peekX, peekY, peekR] = PEEK[index];
  const [endX, endY, endR] = END[index];

  const x = useTransform(scrollYProgress, [a0, a1, b0, b1], [0, peekX, peekX, endX]);
  const y = useTransform(scrollYProgress, [a0, a1, b0, b1], [60, peekY, peekY, endY]);
  const r = useTransform(scrollYProgress, [a0, a1, b0, b1], [0, peekR, peekR, endR]);
  const s = useTransform(scrollYProgress, [a0, a1], [0.5, 1]);

  const cardInner = (
    <>
      <img
        src={photo.img}
        alt={photo.name}
        style={{
          width: "100%",
          height: "auto",
          objectFit: "contain",
          opacity: 1,
          visibility: "visible",
          pointerEvents: "none",
          display: "block",
        }}
      />
      <motion.div
        initial={false}
        animate={{ opacity: cardsOut ? 1 : 0, y: cardsOut ? 0 : 8 }}
        transition={{ duration: 0.5, ease: "easeOut", delay: 0.1 + index * 0.05 }}
        style={{
          position: "absolute",
          top: "calc(100% + 18px)",
          left: 0,
          right: 0,
          textAlign: "center",
        }}
      >
        <div
          style={{
            fontFamily: "'Inter Tight', sans-serif",
            fontSize: 18,
            fontWeight: 500,
            letterSpacing: "-0.3px",
            lineHeight: 1.2,
            color: "#FFFFFF",
          }}
        >
          {photo.name}
        </div>
        <div
          style={{
            fontFamily: "'Inter Tight', sans-serif",
            fontSize: 14,
            fontWeight: 400,
            color: "rgba(255,255,255,0.7)",
            marginTop: 4,
          }}
        >
          {photo.price ?? "€129.90"}
        </div>
      </motion.div>
    </>
  );

  return (
    <motion.div
      style={{
        position: "absolute",
        left: -90,
        top: -90,
        width: 180,
        zIndex: CARD_Z[index],
        pointerEvents: "auto",
        cursor: "pointer",
        opacity: 1,
        visibility: "visible",
        x,
        y,
        rotate: r,
        scale: s,
      }}
      whileHover={{ rotate: [0, -3, 3, -2, 2, 0], transition: { duration: 0.5, ease: "easeInOut" } }}
    >
      {photo.id ? (
        <Link to={`/product/${photo.id}`} style={{ display: "block", textDecoration: "none" }}>
          {cardInner}
        </Link>
      ) : (
        cardInner
      )}
    </motion.div>
  );
}